/** @format */

import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_STATUS_KEY,
  USER_KEY,
  REMEMBER_ME_KEY,
  USER_EMAIL_KEY,
  TOKEN_EXPIRY_BUFFER,
} from "./constants";

/**
 * JWT Token payload interface
 */
export interface JWTPayload {
  user_id?: string | number;
  email?: string;
  exp?: number;
  iat?: number;
  user?: {
    id?: string | number;
    email?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
  };
  [key: string]: any;
}

/**
 * Safely decode JWT token
 * Returns null if decoding fails
 */
import { jwtDecode } from "jwt-decode";

export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    if (!token || typeof token !== "string") {
      return null;
    }
    const payload = jwtDecode<JWTPayload>(token);
    return payload || null;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * Includes a buffer time to refresh before actual expiry
 */
export const isTokenExpired = (
  token: string,
  bufferSeconds: number = TOKEN_EXPIRY_BUFFER
): boolean => {
  try {
    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    // Add buffer to refresh token before it actually expires
    return payload.exp - bufferSeconds < currentTime;
  } catch {
    return true;
  }
};

/**
 * Get token expiry time in seconds
 */
export const getTokenExpiryTime = (token: string): number | null => {
  try {
    const payload = decodeJWT(token);
    return payload?.exp || null;
  } catch {
    return null;
  }
};

/**
 * Clear all authentication data from storage
 * Uses localStorage.clear() and sessionStorage.clear() to ensure all data is removed
 */
export const clearAuthData = (): void => {
  localStorage.clear();
  sessionStorage.clear();
};

/**
 * Clear tokens only (preserve other data like remember me)
 */
export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_STATUS_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Store tokens in localStorage or sessionStorage
 * @param useLocalStorage - If false, uses sessionStorage for better security
 */
export const setTokens = (
  accessToken: string,
  refreshToken: string,
  useLocalStorage: boolean = true
): void => {
  const storage = useLocalStorage ? localStorage : sessionStorage;

  storage.setItem(ACCESS_TOKEN_KEY, accessToken);
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  storage.setItem(AUTH_STATUS_KEY, "true");
};

/**
 * Get access token from storage
 */
export const getAccessToken = (): string | null => {
  return (
    localStorage.getItem(ACCESS_TOKEN_KEY) ||
    sessionStorage.getItem(ACCESS_TOKEN_KEY)
  );
};

/**
 * Get refresh token from storage
 */
export const getRefreshToken = (): string | null => {
  return (
    localStorage.getItem(REFRESH_TOKEN_KEY) ||
    sessionStorage.getItem(REFRESH_TOKEN_KEY)
  );
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  if (!token) return false;

  return !isTokenExpired(token);
};

/**
 * Store user data
 */
export const setUserData = (userData: any): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
};

/**
 * Get stored user data
 */
export const getUserData = (): any | null => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

/**
 * Remember me functionality
 */
export const rememberMeUtils = {
  setRememberMe: (email: string): void => {
    localStorage.setItem(REMEMBER_ME_KEY, "true");
    localStorage.setItem(USER_EMAIL_KEY, email);
  },

  clearRememberMe: (): void => {
    localStorage.removeItem(REMEMBER_ME_KEY);
    localStorage.removeItem(USER_EMAIL_KEY);
  },

  isRememberMeEnabled: (): boolean => {
    return localStorage.getItem(REMEMBER_ME_KEY) === "true";
  },

  getRememberedEmail: (): string | null => {
    return localStorage.getItem(USER_EMAIL_KEY);
  },
};

/**
 * Sanitize user input to prevent XSS
 */
export const sanitizeInput = (input: string): string => {
  const div = document.createElement("div");
  div.textContent = input;
  return div.innerHTML;
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const getAuthStorageKeys = (): string[] => {
  try {
    const keys = Object.keys(localStorage);
    const authKeys = keys.filter((key) => key.startsWith("OPTICLINIC"));
    return authKeys;
  } catch (error) {
    return [];
  }
};

export const getAuthStorageDetails = (): Record<string, string> => {
  try {
    const authKeys = getAuthStorageKeys();
    const details: Record<string, string> = {};

    authKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      details[key] = value ? "***" : "null";
    });

    return details;
  } catch (error) {
    return {};
  }
};
